import {
  CircleLoader,
  Colors,
  LoaderLooks,
  LoaderSizes,
  StandardTooltip
} from '@brandfolder/react';
import { t, Trans } from '@lingui/macro';
import React, { FunctionComponent, useState, useMemo, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import {
  PeopleTaggingStatus
} from '@api/v4/private/people_tagging/faces';
import { usePeopleTaggingFaces } from '@components/asset/modal/tabs/shared/hooks/usePeopleTaggingFaces';

import { FaceOverlay } from './FaceOverlay';
import { BoundingBox } from './types';

const PeopleSection = styled.div``;

const SectionTitle = styled.span<{ $marginBottom?: string }>`
  display: block;
  color: #4a4a4a;
  font-size: 14px;
  font-weight: 700;
  padding-top: 15px;
  text-transform: uppercase;
  font-family: "Circular-Medium", "Arial", "Trebuchet MS", sans-serif;
  margin-bottom: ${({ $marginBottom = '12px' }): string => $marginBottom};
`;

const PeopleGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
`;

const PersonItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 80px;
`;

const PersonImage = styled.img`
  width: 64px;
  height: 64px;
  border-radius: 4px;
  object-fit: cover;
  margin-bottom: 6px;
  cursor: pointer;
`;

const PersonName = styled.span`
  font-size: 12px;
  color: #4a4a4a;
  text-align: center;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
`;

const PersonContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

// const EmptyStateMessage = styled.div`
//   color: #9b9b9b;
//   font-size: 14px;
//   line-height: 26px;
// `;

const LoaderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .identifying-faces-loader {
    width: unset;
  }
`;

const IdentifyingFacesText = styled.span`
  color: #727682;
`;

interface Person {
  id: string;
  name: string;
  imageUrl: string;
  assetCount: number;
  boundingBox: BoundingBox;
  isHidden: boolean;
  identity_id: string;
}

export interface PeopleInAssetProps {
  attachmentKey?: string;
}

export const PeopleInAsset: FunctionComponent<PeopleInAssetProps> = ({
  attachmentKey = undefined,
}) => {
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
  const [people, setPeople] = useState<Person[]>([]);
  const [hoveredPerson, setHoveredPerson] = useState<string | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [overlayContainer, setOverlayContainer] = useState<HTMLDivElement | null>(null);

  const brandfolderKey = BFG?.brandfolder_key;

  // Use shared hook for faces polling
  const { faces: facesResponse, isLoading, status } = usePeopleTaggingFaces({
    attachmentKey,
    brandfolderKey,
    enabled: !!attachmentKey
  });

  const handleImageError = (personId: string): void => {
    setImageErrors((prev) => ({
      ...prev,
      [personId]: true
    }));
  };

  const handlePersonHover = useCallback((personId: string): void => {
    setHoveredPerson(personId);
  }, []);

  const handlePersonLeave = useCallback((): void => {
    setHoveredPerson(null);
  }, []);

  // Transform faces response to Person format
  useEffect(() => {
    if (!facesResponse || facesResponse.length === 0) {
      setPeople([]);
      return;
    }

    const mapped: Person[] = facesResponse.map((f) => {

      const isHidden = JSON.parse(f.identity_metadata || '{}')?.hide || false;

      return {
        id: f.id,
        name: f.identity_name || '', // placeholder until name is resolved
        imageUrl: f.identity_thumbnail_url || f.face_thumbnail_url,
        assetCount: f.face_count || 0,
        isHidden,
        identity_id: f.identity_id,
        boundingBox: {
          height: f.bounding_box.height,
          left: f.bounding_box.left,
          top: f.bounding_box.top,
          width: f.bounding_box.width
        }
      };
    });

    setPeople(mapped);
  }, [facesResponse]);

  // Wait for image to load and create overlay container
  useEffect(() => {
    // Reset state when attachment changes
    setImageLoaded(false);
    setOverlayContainer(null);

    const setupOverlayContainer = (): void => {
      const imageElement = document.getElementById('asset-preview-image') as HTMLImageElement | null;
      const wrapperElement = document.getElementById('asset-preview-wrapper');

      if (!imageElement || !wrapperElement) {
        setImageLoaded(false);
        setOverlayContainer(null);
        return;
      }

      // Check if image is loaded and has dimensions
      if (!imageElement.complete || imageElement.naturalWidth === 0 || imageElement.naturalHeight === 0) {
        setImageLoaded(false);
        setOverlayContainer(null);
        return;
      }

      // Get image's bounding rect relative to wrapper
      const imageRect = imageElement.getBoundingClientRect();
      const wrapperRect = wrapperElement.getBoundingClientRect();

      // Calculate position relative to wrapper
      const left = imageRect.left - wrapperRect.left;
      const top = imageRect.top - wrapperRect.top;
      const width = imageRect.width;
      const height = imageRect.height;

      // Create or update overlay container
      let container = document.getElementById('face-overlay-container') as HTMLDivElement | null;

      if (!container) {
        container = document.createElement('div');
        container.id = 'face-overlay-container';
        container.style.position = 'absolute';
        // Container is transparent and only used for positioning
        // Overlays handle their own pointer events via CSS
        wrapperElement.appendChild(container);
      }

      // Update container position and size to match image
      container.style.left = `${left}px`;
      container.style.top = `${top}px`;
      container.style.width = `${width}px`;
      container.style.height = `${height}px`;

      setOverlayContainer(container);
      setImageLoaded(true);
    };

    // Try to setup immediately if image is already loaded
    setupOverlayContainer();

    // Listen for image load event
    const imageElement = document.getElementById('asset-preview-image') as HTMLImageElement | null;
    if (imageElement && !imageElement.complete) {
      const handleImageLoad = (): void => {
        setupOverlayContainer();
      };

      imageElement.addEventListener('load', handleImageLoad);

      return (): void => {
        imageElement.removeEventListener('load', handleImageLoad);
      };
    }

    // Also listen for window resize to update container position
    const handleResize = (): void => {
      setupOverlayContainer();
    };

    window.addEventListener('resize', handleResize);

    return (): void => {
      window.removeEventListener('resize', handleResize);
      // Clean up container on unmount
      const container = document.getElementById('face-overlay-container');
      if (container) {
        container.remove();
      }
    };
  }, [attachmentKey, people.length]); // Re-run when attachment changes or people are loaded

  const faceOverlaysPortal = useMemo(() => {
    if (!overlayContainer || !imageLoaded || people.length === 0) {
      return null;
    }

    return createPortal(
      <>
        {people.map((person) => (
          <FaceOverlay
            key={person.id}
            boundingBox={person.boundingBox}
            isVisible={hoveredPerson === person.id}
            onHover={() => handlePersonHover(person.id)}
            onLeave={handlePersonLeave}
            personName={person.isHidden ? '' : person.name}
          />
        ))}
      </>,
      overlayContainer
    );
  }, [hoveredPerson, handlePersonHover, handlePersonLeave, people, overlayContainer, imageLoaded]);

  const showLoader = isLoading || status === PeopleTaggingStatus.WaitingForPeopleTagging;
  const onlyVisiblePeople = people.filter((person) => !person.isHidden);
  const noFacesDetected = !showLoader && onlyVisiblePeople.length === 0;
  const showPeople = !showLoader && onlyVisiblePeople.length > 0;

  const handlePersonClick = (personName: string, personIdentityId?: string): void => {
    if (personName) {
      const searchQuery = `people.strict:("${personName}")`;
      const encodedQuery = encodeURIComponent(searchQuery);
      // Store person_id in sessionStorage
      if (personIdentityId) {
        sessionStorage.setItem('people_search_person_id', personIdentityId);
      }
      window.open(`/${BFG.brandfolder_slug}?q=${encodedQuery}`, '_blank');
    }
  };

  if (noFacesDetected) {
    return null;
  }

  return (
    <PeopleSection>
      {/* Pass $marginBottom prop to adjust margin based on showEmpty option */}
      <SectionTitle>
        <Trans>People in Asset</Trans>
      </SectionTitle>

      {showLoader && (
        <LoaderContainer>
          <CircleLoader
            className='identifying-faces-loader'
            label={t`Identifying faces...`}
            look={LoaderLooks.Gray}
            size={LoaderSizes.Small}
          />
          <IdentifyingFacesText>
            <Trans>Identifying faces...</Trans>
          </IdentifyingFacesText>
        </LoaderContainer>
      )}

      {/* {noFacesDetected && (
        <EmptyStateMessage>
          <Trans>No faces detected</Trans>
        </EmptyStateMessage>
      )} */}
      {showPeople && (
        <PeopleGrid>
          {onlyVisiblePeople.map((person) => (
            <PersonItem
              key={person.id}
            >
              <StandardTooltip
                color={Colors.None}
                id={`person-tooltip-${person.id}`}
                tooltip={t`View ${person.assetCount} ${person.assetCount === 1 ? 'asset' : 'assets'}`}
              >
                <PersonContent
                  onClick={() => handlePersonClick(person.name, person.identity_id)}
                  onMouseEnter={() => handlePersonHover(person.id)}
                  onMouseLeave={handlePersonLeave}
                >
                  <PersonImage
                    alt={`${person.name}`}
                    data-testid="person-thumbnail"
                    onError={() => handleImageError(person.id)}
                    src={imageErrors[person.id]
                      ? `${BFG?.icon_service_url || ''}/assets/types/person.svg`
                      : person.imageUrl}
                  />
                  <PersonName>{person.name}</PersonName>
                </PersonContent>
              </StandardTooltip>
            </PersonItem>
          ))}
        </PeopleGrid>
      )}

      {faceOverlaysPortal}
    </PeopleSection>
  );
};
